import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import blogPosts from '../data/blogPosts';
import './BlogPost.css';

function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Simple markdown-like rendering for content
  const renderContent = (content) => {
    const lines = content.trim().split('\n');
    const elements = [];
    let inCodeBlock = false;
    let codeContent = [];
    let codeLanguage = '';
    let inList = false;
    let listItems = [];
    let key = 0;

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(<ul key={key++}>{listItems}</ul>);
        listItems = [];
        inList = false;
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={key++} className="code-block">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          flushList();
          inCodeBlock = true;
          codeLanguage = line.slice(3);
        }
        continue;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        continue;
      }

      // Headers
      if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={key++}>{line.slice(3)}</h2>);
        continue;
      }

      if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={key++}>{line.slice(4)}</h3>);
        continue;
      }

      // Blockquotes
      if (line.startsWith('> ')) {
        flushList();
        elements.push(<blockquote key={key++}>{line.slice(2)}</blockquote>);
        continue;
      }

      // List items
      if (line.startsWith('- ')) {
        inList = true;
        const text = line.slice(2);
        listItems.push(<li key={`li-${key++}`}>{renderInlineMarkdown(text)}</li>);
        continue;
      }

      // Numbered list items
      if (/^\d+\.\s/.test(line)) {
        inList = true;
        const text = line.replace(/^\d+\.\s/, '');
        listItems.push(<li key={`li-${key++}`}>{renderInlineMarkdown(text)}</li>);
        continue;
      }

      // Empty line ends list
      if (line.trim() === '' && inList) {
        flushList();
        continue;
      }

      // Regular paragraphs
      if (line.trim() !== '') {
        flushList();
        elements.push(<p key={key++}>{renderInlineMarkdown(line)}</p>);
      }
    }

    flushList();
    return elements;
  };

  const renderInlineMarkdown = (text) => {
    // Handle **bold**, *italic*, `code`, and [links](/url)
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Bold
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      // Italic
      const italicMatch = remaining.match(/\*(.+?)\*/);
      // Inline code
      const codeMatch = remaining.match(/`(.+?)`/);
      // Links
      const linkMatch = remaining.match(/\[(.+?)\]\((.+?)\)/);

      const matches = [
        boldMatch && { type: 'bold', match: boldMatch, index: boldMatch.index },
        italicMatch && { type: 'italic', match: italicMatch, index: italicMatch.index },
        codeMatch && { type: 'code', match: codeMatch, index: codeMatch.index },
        linkMatch && { type: 'link', match: linkMatch, index: linkMatch.index },
      ].filter(Boolean).sort((a, b) => a.index - b.index);

      if (matches.length === 0) {
        parts.push(remaining);
        break;
      }

      const first = matches[0];
      
      if (first.index > 0) {
        parts.push(remaining.slice(0, first.index));
      }

      if (first.type === 'bold') {
        parts.push(<strong key={key++}>{first.match[1]}</strong>);
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'italic' && !boldMatch) {
        parts.push(<em key={key++}>{first.match[1]}</em>);
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'code') {
        parts.push(<code key={key++} className="inline-code">{first.match[1]}</code>);
        remaining = remaining.slice(first.index + first.match[0].length);
      } else if (first.type === 'link') {
        parts.push(
          <Link key={key++} to={first.match[2]}>{first.match[1]}</Link>
        );
        remaining = remaining.slice(first.index + first.match[0].length);
      } else {
        // Skip this match if it's an italic inside a bold
        parts.push(remaining.slice(0, first.index + 1));
        remaining = remaining.slice(first.index + 1);
      }
    }

    return parts;
  };

  return (
    <section className="page blog-post">
      <Link to="/blog" className="back-link">← Back to Blog</Link>
      
      <article>
        <header className="post-header">
          <h1>{post.title}</h1>
          <div className="post-meta">
            <span className="post-date">{post.date}</span>
            <span className="post-author">by {post.author}</span>
          </div>
        </header>

        <div className="post-content">
          {renderContent(post.content)}
        </div>
      </article>
    </section>
  );
}

export default BlogPost;
